/* eslint-disable @typescript-eslint/naming-convention, complexity, max-lines-per-function */
import {useContext, useMemo} from 'react';
import {NEXT_PUBLIC_MASTER_HOST} from 'core/helpers/env';
import {useListingContext} from 'plugins/modules/listing/context';
import {IProductShort} from 'modules/products/types';
import {FilterContext} from 'plugins/modules/filter/context/new';
import {usePathname} from 'next/navigation';
import {useProductsTileViewOnTablet} from 'modules/products/hooks/useProductsTileViewOnTablet';
import {buildProductsSchemaJson, computeMinMaxPrices} from 'core/seo/schema/catalogSchemas';
import {IBreadcrumb} from 'modules/seo/types';
import {useCatalogBreadcrumbs} from './useCatalogBreadcrumbs';
import {ListingBreadCrumbs} from 'modules/product-card/helpers/listing';

export const useCatalogPageState = (initialBreadcrumbs: IBreadcrumb[] = []) => {
    const {breadcrumbs: breadcrumbsFromSeo} = useCatalogBreadcrumbs(initialBreadcrumbs);
    const {meta, seo, pagination, list, isFetching} = useListingContext<IProductShort>();
    const {appliedFilters, isLoading} = useContext(FilterContext);
    const pathname = usePathname();

    const hasInterestingSections = Boolean(meta?.interestingSections?.length);
    const isFirstPage = 1 === pagination.pageNumber;

    useProductsTileViewOnTablet();

    const ogImage = `${NEXT_PUBLIC_MASTER_HOST}${list?.[0]?.media?.imagePreview || ''}`;
    const ogUrl = `${NEXT_PUBLIC_MASTER_HOST}${pathname}`;

    const {minPrice, maxPrice} = useMemo(() => computeMinMaxPrices(list), [list]);

    const productSchemaJson = useMemo(() => {
        return buildProductsSchemaJson({
            products: list,
            name: seo?.pageTitle || '',
            description: seo?.metaDescription || undefined,
            image: ogImage,
            offerCount: pagination?.totalCount || list?.length,
        });
    }, [list, seo?.pageTitle, seo?.metaDescription, pagination?.totalCount, ogImage]);

    const breadcrumbs = useMemo(() => {
        const filterBreadcrumbs = new ListingBreadCrumbs(appliedFilters, seo, pathname).getBreadcrumbsList();

        if (0 < breadcrumbsFromSeo.length) {
            if (0 < filterBreadcrumbs.length) {
                return [...breadcrumbsFromSeo, ...filterBreadcrumbs];
            }
            return breadcrumbsFromSeo;
        }
        return [];
    }, [appliedFilters, breadcrumbsFromSeo, pathname, seo]);

    return {
        isFetching,
        isLoading,
        hasInterestingSections,
        isFirstPage,
        breadcrumbs,
        ogImage,
        ogUrl,
        productSchemaJson,
        seo,
        minPrice,
        maxPrice,
        appliedFilters,
        pagination,
        meta,
    };
};
